import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { setSentryTags } from "@/lib/sentry";
import { isSupportedLanguage, type SupportedLanguage } from "./config";
import { persistLanguage } from "./storage";

/**
 * Reads profiles.preferred_language for the current user once after login and
 * applies it to i18next. Subsequent switcher clicks call writeProfileLanguage()
 * below; we don't re-read on every render to keep auth path cheap.
 *
 * Mount once inside <BrowserRouter>, after LanguageSync.
 */
export const ProfileLanguageSync = () => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("preferred_language, business_id")
        // profiles keys the auth user by user_id (id is the row PK) — matching
        // on id silently returned nothing, so the preference never loaded.
        .eq("user_id", userId)
        .maybeSingle();
      if (cancelled || error || !data) return;
      const lng = data.preferred_language;
      if (isSupportedLanguage(lng) && i18n.language !== lng) {
        await i18n.changeLanguage(lng);
        persistLanguage(lng);
      }
      // Tag Sentry with the user's locale + business so production
      // errors carry the context that's hardest to reconstruct from
      // a stack trace alone (i18n-specific bugs, tenant-isolation
      // issues). No-op for public surfaces (Sentry isn't initialized
      // before the dashboard mounts).
      void setSentryTags({
        locale: i18n.language,
        businessId: data.business_id,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, i18n]);

  // Keep the Sentry locale tag in sync when the user toggles language
  // via the header dropdown — without this, the tag stays whatever was
  // read at login time.
  useEffect(() => {
    const onLang = (lng: string) => void setSentryTags({ locale: lng });
    i18n.on("languageChanged", onLang);
    return () => {
      i18n.off("languageChanged", onLang);
    };
  }, [i18n]);

  return null;
};

export async function writeProfileLanguage(
  userId: string,
  lng: SupportedLanguage
): Promise<void> {
  const { error } = await supabase
    .from("profiles")
    .update({ preferred_language: lng })
    .eq("user_id", userId);
  if (error) {
    // Non-fatal: localStorage + URL prefix still keep the choice working
    // for the active session. Log only.
    console.warn("Failed to persist preferred_language:", error.message);
  }
}
